import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import PageLayout from '../components/PageLayout';
import EnrichmentEditor from '../components/EnrichmentEditor';
import './ArtistsPage.css';

interface Artist {
  id: string;
  name: string;
  source?: string; // "ticketmaster", "etix", "eventim", "scraper", or undefined (none)
  last_updated?: string;
  created_at?: string;
  spotify_match_confidence?: string;
}

const ArtistsPage: React.FC = () => {
  const { token } = useAuth();
  const [artists, setArtists] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingArtist, setEditingArtist] = useState<Artist | null>(null);
  const [formData, setFormData] = useState<Partial<Artist>>({});
  const [showEnrichmentEditor, setShowEnrichmentEditor] = useState(false);
  const [enrichmentEntityId, setEnrichmentEntityId] = useState<string>('');
  const [enrichmentEntityName, setEnrichmentEntityName] = useState<string>('');
  const [enrichmentType, setEnrichmentType] = useState<'ticketmaster' | 'spotify' | 'etix' | 'eventim'>('ticketmaster');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('all'); // 'all', 'ticketmaster', 'etix', 'eventim', 'scraper', 'none'
  const [spotifyMatchFilter, setSpotifyMatchFilter] = useState<string>('all');
  const [lastKey, setLastKey] = useState<string>('');
  const [hasMore, setHasMore] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (token) {
      loadArtists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!token) return;

    // Reset pagination when search term changes
    setLastKey('');
    
    searchTimeoutRef.current = setTimeout(() => {
      loadArtists();
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const loadArtists = useCallback(async (paginationKey?: string) => {
    if (!token) return;
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('limit', '100');
      
      // Add search query if provided
      if (searchTerm.trim().length >= 2) {
        params.append('q', searchTerm.trim());
      }
      
      // Add pagination key if provided
      if (paginationKey) {
        params.append('last_key', paginationKey);
      }
      
      const response = await apiRequest<{ artists: Artist[] | null; count: number; last_key?: string }>(
        `/admin/artists?${params.toString()}`,
        token
      );
      
      if (paginationKey) {
        // Append to existing results for pagination
        setArtists(prev => [...prev, ...(response.artists || [])]);
      } else {
        // Replace results for new search or initial load
        setArtists(response.artists || []);
      }
      
      setLastKey(response.last_key || '');
      setHasMore(!!response.last_key);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load artists');
      if (!paginationKey) {
        setArtists([]);
      }
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm]);

  const loadMore = () => {
    if (lastKey && !loading) {
      loadArtists(lastKey);
    }
  };

  const handleCreate = () => {
    setEditingArtist(null);
    setFormData({});
    setShowForm(true);
  };

  const handleEdit = (artist: Artist) => {
    setEditingArtist(artist);
    setFormData(artist);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!token || !window.confirm('Are you sure you want to delete this artist?')) {
      return;
    }
    try {
      await apiRequest(`/admin/artists/${id}`, token, { method: 'DELETE' });
      // Reset and reload
      setLastKey('');
      await loadArtists();
    } catch (err: any) {
      setError(err.message || 'Failed to delete artist');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    try {
      if (editingArtist) {
        await apiRequest(`/admin/artists/${editingArtist.id}`, token, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
      } else {
        await apiRequest('/admin/artists', token, {
          method: 'POST',
          body: JSON.stringify(formData),
        });
      }
      setShowForm(false);
      // Reset and reload
      setLastKey('');
      await loadArtists();
    } catch (err: any) {
      setError(err.message || 'Failed to save artist');
    }
  };

  const getFilteredArtists = () => {
    let filtered = artists;

    // Filter by source
    if (sourceFilter !== 'all') {
      if (sourceFilter === 'none') {
        filtered = filtered.filter(a => !a.source || a.source === '');
      } else {
        filtered = filtered.filter(a => a.source === sourceFilter);
      }
    }

    // Apply Spotify match filter (client-side since backend doesn't support it)
    if (spotifyMatchFilter !== 'all') {
      filtered = filtered.filter(artist => {
        const match = artist.spotify_match_confidence || 'none';
        if (spotifyMatchFilter === 'no-match') {
          return match === 'none' || !match;
        }
        return match === spotifyMatchFilter;
      });
    }

    // Note: Search is now handled server-side when searchTerm.length >= 2
    // Only apply client-side filtering if search term is less than 2 characters
    if (searchTerm.trim().length > 0 && searchTerm.trim().length < 2) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(artist => {
        const nameMatch = artist.name?.toLowerCase().includes(term);
        const idMatch = artist.id?.toLowerCase().includes(term);
        return nameMatch || idMatch;
      });
    }

    return filtered;
  };

  if (loading && artists.length === 0) {
    return (
      <PageLayout title="Artists">
        <div>Loading...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Artists">
      {error && <div className="error-banner">{error}</div>}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '1rem', flex: 1, minWidth: '300px' }}>
          <div style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by name or ID... (min 2 characters)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                paddingRight: loading ? '2.5rem' : '0.75rem'
              }}
            />
            {loading && (
              <span style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#666'
              }}>
                ...
              </span>
            )}
          </div>
          <div style={{ minWidth: '200px' }}>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: 'white'
              }}
            >
              <option value="all">All Sources</option>
              <option value="etix">Etix</option>
              <option value="eventim">EventIM</option>
              <option value="scraper">Scraper</option>
              <option value="ticketmaster">Ticketmaster</option>
              <option value="none">No Source</option>
            </select>
          </div>
          <div style={{ minWidth: '200px' }}>
            <select
              value={spotifyMatchFilter}
              onChange={(e) => setSpotifyMatchFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                backgroundColor: 'white'
              }}
            >
              <option value="all">All Spotify Matches</option>
              <option value="exact">Exact Match</option>
              <option value="fuzzy">Fuzzy Match</option>
              <option value="low">Low Match</option>
              <option value="manual">Manual Match</option>
              <option value="none">No Match</option>
              <option value="no-match">No Match Data</option>
            </select>
          </div>
        </div>
        <button onClick={handleCreate} className="btn-primary">
          Create Artist
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingArtist ? 'Edit Artist' : 'Create Artist'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingArtist ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Source</th>
              <th>Spotify Match</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getFilteredArtists().length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  {loading ? 'Loading...' : 'No artists found. Create your first artist to get started.'}
                </td>
              </tr>
            ) : (
              getFilteredArtists().map((artist) => (
              <tr key={artist.id}>
                <td>{artist.id}</td>
                <td>{artist.name}</td>
                <td>
                  {artist.source ? (
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      backgroundColor: artist.source === 'ticketmaster' ? '#e3f2fd' : artist.source === 'scraper' ? '#e8f5e9' : '#fff3e0',
                      color: artist.source === 'ticketmaster' ? '#1976d2' : artist.source === 'scraper' ? '#2e7d32' : '#e65100',
                      textTransform: 'capitalize'
                    }}>
                      {artist.source === 'eventim' ? 'EventIM' : artist.source === 'scraper' ? 'Scraper' : artist.source}
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </td>
                <td>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    backgroundColor: 
                      artist.spotify_match_confidence === 'exact' ? '#d4edda' :
                      artist.spotify_match_confidence === 'manual' ? '#cfe2ff' :
                      artist.spotify_match_confidence === 'fuzzy' ? '#fff3cd' :
                      artist.spotify_match_confidence === 'low' ? '#f8d7da' :
                      artist.spotify_match_confidence === 'none' ? '#e2e3e5' :
                      'transparent',
                    color:
                      artist.spotify_match_confidence === 'exact' ? '#155724' :
                      artist.spotify_match_confidence === 'manual' ? '#084298' :
                      artist.spotify_match_confidence === 'fuzzy' ? '#856404' :
                      artist.spotify_match_confidence === 'low' ? '#721c24' :
                      artist.spotify_match_confidence === 'none' ? '#383d41' :
                      '#6c757d'
                  }}>
                    {artist.spotify_match_confidence || 'No data'}
                  </span>
                </td>
                <td className="actions-cell">
                  <div className="actions-buttons">
                    <button onClick={() => handleEdit(artist)} className="btn-sm">
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setEnrichmentEntityId(artist.id);
                        setEnrichmentType('ticketmaster');
                        setShowEnrichmentEditor(true);
                      }}
                      className="btn-sm btn-enrichment"
                    >
                      Edit Ticketmaster
                    </button>
                    <button
                      onClick={() => {
                        setEnrichmentEntityId(artist.id);
                        setEnrichmentType('etix');
                        setShowEnrichmentEditor(true);
                      }}
                      className="btn-sm btn-enrichment"
                    >
                      Edit Etix
                    </button>
                    <button
                      onClick={() => {
                        setEnrichmentEntityId(artist.id);
                        setEnrichmentType('eventim');
                        setShowEnrichmentEditor(true);
                      }}
                      className="btn-sm btn-enrichment"
                    >
                      Edit EventIM
                    </button>
                    <button
                      onClick={() => {
                        setEnrichmentEntityId(artist.id);
                        setEnrichmentEntityName(artist.name);
                        setEnrichmentType('spotify');
                        setShowEnrichmentEditor(true);
                      }}
                      className="btn-sm btn-enrichment-spotify"
                    >
                      Edit Spotify
                    </button>
                    <button
                      onClick={() => handleDelete(artist.id)}
                      className="btn-sm btn-danger"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <button
            onClick={loadMore}
            disabled={loading}
            className="btn-primary"
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Loading...' : 'Load More Artists'}
          </button>
        </div>
      )}

      {showEnrichmentEditor && (
        <EnrichmentEditor
          entityType="artist"
          entityId={enrichmentEntityId}
          enrichmentType={enrichmentType}
          entityName={enrichmentType === 'spotify' ? enrichmentEntityName : undefined}
          onClose={() => {
            setShowEnrichmentEditor(false);
            setEnrichmentEntityId('');
            setEnrichmentEntityName('');
          }}
        />
      )}
    </PageLayout>
  );
};

export default ArtistsPage;

